import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const OFFSET_VAR = '--impersonation-banner-offset';

function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Shown when an admin is viewing Landing via shared cp_jwt impersonation cookie.
 * Renders above the fixed navbar; exit happens in the client panel.
 */
export default function ImpersonationBanner() {
  const [active, setActive] = useState(false);
  const [label, setLabel] = useState('user');
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const bannerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE?.replace(/\/$/, '') || '';
        const token = (() => {
          try { return localStorage.getItem('authToken'); } catch (_) { return null; }
        })();
        const response = await fetch(`${API_BASE}/auth/status`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });
        if (!response.ok) {
          if (!cancelled) {
            setActive(false);
            setExpiresAt(null);
          }
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        const impersonating = Boolean(data?.logged_in && data?.impersonatedBy);
        setActive(impersonating);
        if (impersonating && data?.user) {
          const name = `${data.user.firstName || ''} ${data.user.surName || ''}`.trim();
          setLabel(name || data.user.email || 'user');
          const exp = Number(data.expiresAt);
          setExpiresAt(Number.isFinite(exp) && exp > 0 ? exp : null);
        } else {
          setExpiresAt(null);
        }
      } catch (_) {
        if (!cancelled) {
          setActive(false);
          setExpiresAt(null);
        }
      }
    };

    sync();
    const onVis = () => { if (!document.hidden) sync(); };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (!active || !expiresAt) return undefined;
    const tick = () => {
      const left = expiresAt - Date.now();
      setRemainingMs(left);
      if (left <= 0) {
        setActive(false);
        setExpiresAt(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, expiresAt]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const clearOffset = () => {
      root.style.removeProperty(OFFSET_VAR);
      root.classList.remove('impersonation-banner-active');
    };

    if (!active) {
      clearOffset();
      return clearOffset;
    }

    const applyOffset = () => {
      const height = bannerRef.current?.offsetHeight || 44;
      root.style.setProperty(OFFSET_VAR, `${height}px`);
      root.classList.add('impersonation-banner-active');
    };

    applyOffset();
    window.addEventListener('resize', applyOffset);
    return () => {
      window.removeEventListener('resize', applyOffset);
      clearOffset();
    };
  }, [active, label, remainingMs]);

  if (!active) return null;

  const panelUrl = (process.env.REACT_APP_TV_CL_PANEL_URL || 'https://v2.trivox.sh').replace(/\/$/, '');
  const urgent = remainingMs > 0 && remainingMs <= 5 * 60 * 1000;

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 inset-x-0 z-[100001] border-b border-amber-600/30 bg-amber-500 text-gray-950"
      role="status"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm font-medium">
        <p className="min-w-0">
          Admin impersonation active for <span className="font-semibold">{label}</span>
          <span className={`ml-2 inline-flex items-center rounded-md px-2 py-0.5 font-mono text-xs tabular-nums ${
            urgent ? 'bg-red-700 text-white' : 'bg-black/15 text-gray-950'
          }`}>
            {formatCountdown(remainingMs)} left
          </span>
        </p>
        <a
          href={panelUrl}
          className="rounded-md bg-gray-950 px-2.5 py-1 text-xs font-semibold text-amber-300 hover:bg-black shrink-0"
        >
          Back to panel
        </a>
      </div>
    </div>
  );
}
